<template>
  <!-- Aviso persistente para grace/lapsed. No dismissible. -->
  <subscription-status-banner v-if="isSubscriptionInGrace || isSubscriptionLapsed" />
  <router-view />
  <!-- Asistente flotante: solo con sesión y dentro de la app (no en páginas públicas). -->
  <assistant-dock v-if="showAssistant" />
  <!-- Modo demo (píldora flotante, solo móvil: en desktop va en el nav del Header). -->
  <demo-switcher />
  <!-- Capas de plan, montadas una sola vez (teleport a body). -->
  <upgrade-overlay :open="isUpgradeOpen" @close="closeUpgrade">
    <upgrade-content @navigate="closeUpgrade" />
  </upgrade-overlay>
  <!-- Sale sola cuando `lapsed`, sin esperar ningún 403. -->
  <upgrade-overlay :open="isRenewalOpen" @close="dismissRenewal">
    <renewal-content />
  </upgrade-overlay>
</template>
<script>
// import { onMounted } from 'vue';
// import { useLocalStorage } from './composables/useLocalStorage';
import { onMounted, onBeforeUnmount, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import AssistantDock from '@/components/assistant/AssistantDock.vue';
import DemoSwitcher from '@/components/demo/DemoSwitcher.vue';
import { assistantEnabled, setAssistantEnabled } from '@/composables/useAssistantDock';
import UpgradeOverlay from '@/components/subscription/UpgradeOverlay.vue';
import UpgradeContent from '@/components/subscription/UpgradeContent.vue';
import RenewalContent from '@/components/subscription/RenewalContent.vue';
import SubscriptionStatusBanner from '@/components/subscription/SubscriptionStatusBanner.vue';
import { useUpgradeOverlay } from '@/composables/useUpgradeOverlay';
import { useRenewalOverlay } from '@/composables/useRenewalOverlay';
import { useSubscription } from '@/composables/useSubscription';
import { useCompany } from '@/composables/useCompany';
import { useUserStore } from '@/store/userStore';
import { useLocalStorage } from '@/composables/useLocalStorage';

export default {
  name: 'App',
  components: { UpgradeOverlay, UpgradeContent, RenewalContent, SubscriptionStatusBanner, AssistantDock, DemoSwitcher },
  setup: () => {
    // const { getLocalAccessToken } = useLocalStorage();
    // const isAuthenticated = getLocalAccessToken();

    // const loadScript = () => {
    //   const script = document.createElement('script');
    //   script.id = 'writwiz-connector';
    //   script.src = 'https://api.chronox.ai/chatbot.js?integration_token=hXBoxqYe1729103859715';
    //   document.body.appendChild(script);
    // };

    // onMounted(() => {
    //   if (isAuthenticated) { // Verifica si el usuario está logueado
    //     loadScript();
    //   }
    // });

    const { isOpen: isUpgradeOpen, closeUpgrade } = useUpgradeOverlay();
    const { open: isRenewalOpen, dismiss: dismissRenewal, notifyExpired: remindRenewal } = useRenewalOverlay();
    const { isSubscriptionInGrace, isSubscriptionLapsed } = useSubscription();

    // Recordatorio periódico: si sigue lapsed y el modal está cerrado, lo reabre.
    const RENEWAL_REMINDER_MS = 15 * 60 * 1000;
    let renewalReminderTimer = null;

    // Refresca la empresa al volver el foco (sesión larga con dato viejo). Throttled a 60s.
    const { onGetCompany } = useCompany();
    const { getLocalAccessToken } = useLocalStorage();

    // Asistente: solo con sesión y en rutas de la app autenticada (las que
    // declaran `meta.authorize`); nunca en páginas públicas.
    const route = useRoute();
    // Oculto por defecto (en prueba): ?assistant=1 en cualquier URL lo activa en
    // ese navegador; ?assistant=0 lo vuelve a ocultar (ver useAssistantDock).
    watch(() => route.query.assistant, (v) => {
      if (v === undefined) return;
      setAssistantEnabled(v === '1' || v === 'true' || v === '');
    }, { immediate: true });
    const showAssistant = computed(() =>
      assistantEnabled.value && !!getLocalAccessToken() && route.matched.some(r => r.meta?.authorize !== undefined));
    const userStore = useUserStore();
    let lastRefresh = 0;
    const onVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!getLocalAccessToken()) return;
      if (Date.now() - lastRefresh < 60_000) return;
      lastRefresh = Date.now();
      const company = await onGetCompany();
      if (company) userStore.loadCompany(company);
    };
    onMounted(() => {
      document.addEventListener('visibilitychange', onVisibilityChange);
      renewalReminderTimer = setInterval(() => {
        if (isSubscriptionLapsed.value) remindRenewal();
      }, RENEWAL_REMINDER_MS);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(renewalReminderTimer);
    });

    return {
      isUpgradeOpen, closeUpgrade,
      isRenewalOpen, dismissRenewal,
      isSubscriptionInGrace, isSubscriptionLapsed,
      showAssistant,
    };
  }
}
</script>
